import { useState, useEffect } from 'react';
import { initializeAnalytics, safeGtag } from '../utils/analytics';

// Simple tracking function
const trackConsentDecision = (decision) => {
  const timestamp = Date.now();
  localStorage.setItem('giveTechCookieConsent', decision);
  localStorage.setItem(`consent_${timestamp}`, decision);
  localStorage.setItem(`consent_${timestamp}_timestamp`, timestamp.toString());
  console.log(`📊 Consent tracked: ${decision}`);
};

// Inline styles as a fallback
const bannerStyles = `
  .cookie-consent-banner {
    position: fixed;
    bottom: -100%;
    left: 0;
    right: 0;
    width: 100%;
    background: #2B373B;
    color: white;
    z-index: 9999;
    transition: bottom 0.5s ease-in-out;
    box-shadow: 0 -2px 20px rgba(0, 0, 0, 0.3);
    border-top: 1px solid #4a5568;
  }

  .cookie-consent-banner.visible {
    bottom: 0;
  }

  .cookie-content {
    max-width: 1200px;
    margin: 0 auto;
    padding: 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 20px;
  }

  .cookie-text {
    flex: 1;
  }

  .cookie-text h3 {
    margin: 0 0 8px 0;
    font-size: 1.2rem;
    font-weight: 600;
  }

  .cookie-text p {
    margin: 0;
    font-size: 0.9rem;
    line-height: 1.4;
    color: #e2e8f0;
  }

  .privacy-link {
    color: #87CEEB;
    text-decoration: underline;
    margin-left: 4px;
  }

  .privacy-link:hover {
    color: #63b3ed;
  }

  .cookie-actions {
    display: flex;
    gap: 12px;
    flex-shrink: 0;
  }

  .cookie-btn {
    padding: 10px 20px;
    border: none;
    border-radius: 6px;
    font-size: 0.9rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;
  }

  .cookie-btn.primary {
    background: #4e503b;
    color: white;
  }

  .cookie-btn.primary:hover {
    background: #5a5c45;
    transform: translateY(-1px);
  }

  .cookie-btn.secondary {
    background: transparent;
    color: white;
    border: 1px solid #cbd5e0;
  }

  .cookie-btn.secondary:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: white;
  }

  @media (max-width: 768px) {
    .cookie-content {
      flex-direction: column;
      text-align: center;
      padding: 15px;
    }
    
    .cookie-actions {
      width: 100%;
      justify-content: center;
    }
    
    .cookie-btn {
      flex: 1;
      max-width: 140px;
    }
  }

  @media (max-width: 480px) {
    .cookie-actions {
      flex-direction: column;
    }
    
    .cookie-btn {
      max-width: none;
    }
  }
`;

export default function CookieConsentBanner() {
  const [isVisible, setIsVisible] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    // Add styles to document head
    const styleElement = document.createElement('style');
    styleElement.textContent = bannerStyles;
    document.head.appendChild(styleElement);

    const hasConsent = localStorage.getItem('giveTechCookieConsent');
    
    if (!hasConsent) {
      setIsMounted(true);
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        setIsVisible(true);
      }, 1000); // Increased delay to ensure everything is loaded
    }

    return () => {
      // Clean up styles when component unmounts
      if (styleElement.parentNode) {
        styleElement.parentNode.removeChild(styleElement);
      }
    };
  }, []);

  const handleConsent = (decision) => {
    // Track the decision
    trackConsentDecision(decision);
    
    // Hide banner immediately
    setIsVisible(false);
    
    // Initialize analytics
    initializeAnalytics();
    
    // Track the consent event
    safeGtag('event', 'cookie_consent', {
      event_category: 'consent',
      event_label: decision
    });
    
    // Remove from DOM after animation
    setTimeout(() => {
      setIsMounted(false);
    }, 500);
  };

  const acceptCookies = () => handleConsent('accepted');
  const rejectCookies = () => handleConsent('rejected');

  if (!isMounted) return null;

  return (
    <div 
      className={`cookie-consent-banner ${isVisible ? 'visible' : ''}`}
      role="dialog"
      aria-label="Cookie consent"
    >
      <div className="cookie-content">
        <div className="cookie-text">
          <h3>🍪 We Use Cookies</h3>
          <p>
            GiveTech uses cookies to enhance your experience, analyze site traffic, 
            and help us understand how we can better serve the visually impaired community. 
            <a href="/privacy-policy" className="privacy-link"> 
              Learn more in our Privacy Policy
            </a>
          </p>
        </div>
        
        <div className="cookie-actions">
          <button 
            onClick={rejectCookies}
            className="cookie-btn secondary"
            aria-label="Reject non-essential cookies"
          >
            Necessary Only
          </button>
          <button 
            onClick={acceptCookies}
            className="cookie-btn primary"
            aria-label="Accept all cookies"
          >
            Accept All
          </button>
        </div>
      </div>
    </div>
  );
}